import { isNativeMobileApp } from './mobile-native';

const GOOGLE_ADS_CONFIG_URL = '/api/system-settings/google-ads';
const ADSENSE_SCRIPT_ID = 'google-adsense-script';

const DEFAULT_GOOGLE_ADS_CONFIG = Object.freeze({
  adsensePublisherId: '',
  webTimelineEnabled: false,
  webTimelineSlotId: '',
  webTimelineLayoutKey: '',
  webTimelineInterval: 5,
  webEntryFullscreenEnabled: false,
  webEntryFullscreenSlotId: '',
  webEntryFullscreenFrequency: 3,
  videoFallbackImageEnabled: false,
  videoFallbackImageSlotId: '',
  videoFallbackImageSkipSeconds: 5,
  videoFallbackImageInterval: 1,
  androidAdMobAppId: '',
  androidBannerAdUnitId: '',
  androidInterstitialAdUnitId: '',
  androidAppOpenAdUnitId: '',
  videoImaEnabled: false,
  videoImaAdTagUrl: '',
  videoImaStartEnabled: true,
  videoImaMidrollIntervalSeconds: 0,
  videoImaMinDurationMinutes: 0,
});

const normalizeString = (value) => String(value || '').trim();

const normalizeInteger = ({ value, fallback, min }) => {
  const parsed = Number.parseInt(value, 10);
  if (!Number.isFinite(parsed)) return fallback;
  return Math.max(min, parsed);
};

export const normalizeAdsensePublisherId = (value) => {
  const normalized = normalizeString(value);
  if (!normalized) return '';
  if (/^pub-\d+$/u.test(normalized)) return `ca-${normalized}`;
  return normalized;
};

export const normalizeAdsenseSlotId = (value) => {
  const normalized = normalizeString(value);
  return /^\d{6,}$/u.test(normalized) ? normalized : '';
};

export const normalizeGoogleAdsConfig = (input = {}) => {
  const config = {
    ...DEFAULT_GOOGLE_ADS_CONFIG,
    ...(input || {}),
  };

  return {
    ...config,
    adsensePublisherId: normalizeAdsensePublisherId(config.adsensePublisherId),
    webTimelineEnabled: Boolean(config.webTimelineEnabled),
    webTimelineSlotId: normalizeAdsenseSlotId(config.webTimelineSlotId),
    webTimelineLayoutKey: normalizeString(config.webTimelineLayoutKey),
    webTimelineInterval: Math.max(
      1,
      Number.parseInt(config.webTimelineInterval, 10) || 5
    ),
    webEntryFullscreenEnabled: Boolean(config.webEntryFullscreenEnabled),
    webEntryFullscreenSlotId: normalizeAdsenseSlotId(
      config.webEntryFullscreenSlotId
    ),
    webEntryFullscreenFrequency: Math.max(
      1,
      Number.parseInt(config.webEntryFullscreenFrequency, 10) || 3
    ),
    videoFallbackImageEnabled: Boolean(config.videoFallbackImageEnabled),
    videoFallbackImageSlotId: normalizeAdsenseSlotId(
      config.videoFallbackImageSlotId
    ),
    videoFallbackImageSkipSeconds: normalizeInteger({
      value: config.videoFallbackImageSkipSeconds,
      fallback: 5,
      min: 0,
    }),
    videoFallbackImageInterval: normalizeInteger({
      value: config.videoFallbackImageInterval,
      fallback: 1,
      min: 1,
    }),
    androidAdMobAppId: normalizeString(config.androidAdMobAppId),
    androidBannerAdUnitId: normalizeString(config.androidBannerAdUnitId),
    androidInterstitialAdUnitId: normalizeString(
      config.androidInterstitialAdUnitId
    ),
    androidAppOpenAdUnitId: normalizeString(config.androidAppOpenAdUnitId),
    videoImaEnabled: Boolean(config.videoImaEnabled),
    videoImaAdTagUrl: normalizeString(config.videoImaAdTagUrl),
    videoImaStartEnabled: Boolean(config.videoImaStartEnabled),
    videoImaMidrollIntervalSeconds: normalizeInteger({
      value: config.videoImaMidrollIntervalSeconds,
      fallback: 0,
      min: 0,
    }),
    videoImaMinDurationMinutes: normalizeInteger({
      value: config.videoImaMinDurationMinutes,
      fallback: 0,
      min: 0,
    }),
  };
};

export const fetchGoogleAdsConfig = async () => {
  const response = await fetch(GOOGLE_ADS_CONFIG_URL);
  if (!response.ok) return normalizeGoogleAdsConfig();
  const data = await response.json();
  return normalizeGoogleAdsConfig(data || {});
};

export const canRenderAdsenseOnCurrentRuntime = () => !isNativeMobileApp();

export const ensureAdsenseScript = (publisherId) => {
  const normalizedPublisherId = normalizeAdsensePublisherId(publisherId);
  if (
    !normalizedPublisherId ||
    !canRenderAdsenseOnCurrentRuntime() ||
    typeof document === 'undefined'
  ) {
    return false;
  }

  if (document.getElementById(ADSENSE_SCRIPT_ID)) {
    return true;
  }

  window.adsbygoogle = window.adsbygoogle || [];

  const script = document.createElement('script');
  script.id = ADSENSE_SCRIPT_ID;
  script.async = true;
  script.crossOrigin = 'anonymous';
  script.src = `https://pagead2.googlesyndication.com/pagead/js/adsbygoogle.js?client=${normalizedPublisherId}`;
  document.head.appendChild(script);

  return true;
};

export const pushAdsenseRender = () => {
  if (
    !canRenderAdsenseOnCurrentRuntime() ||
    typeof window === 'undefined' ||
    !window.adsbygoogle ||
    typeof window.adsbygoogle.push !== 'function'
  ) {
    return false;
  }

  try {
    window.adsbygoogle.push({});
    return true;
  } catch (_error) {
    return false;
  }
};
